import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { orderBy } from 'lodash';
import { finalize } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { AccessLogRecord } from '../../../types';
import { DatePipe } from '@angular/common';
import { UiModule } from '../../../components/ui.module';
import { LocationService } from '../../../service/location.service';

@Component({
  selector: 'app-access-log-dialog',
  templateUrl: './access-log-dialog.html',
  standalone: true,
  imports: [DatePipe, UiModule],
})
export class AccessLogDialogComponent implements OnInit {
  private locationService = inject(LocationService);

  public branchId = input.required<number>();
  public locationId = input.required<number>();
  public allShare = input.required<string>();
  public recentShare = input.required<string>();

  public branchIdNum = computed(() => +this.branchId());
  public locationIdNum = computed(() => +this.locationId());

  accessLog = signal<AccessLogRecord[] | undefined>(undefined);

  branchViewers = computed(() => {
    const viewers = this.accessLog()?.filter((al) => this.isBranchViewer(al) && al.cases_accessed && al.cases_accessed.length > 0) ?? [];
    return orderBy(viewers, [(v) => this.getMaxLastAccessed(v)], ['desc']).map((viewer) => ({
      ...viewer,
      latestAccessDate: this.getLatestAccessDate(viewer),
    }));
  });

  clientViewers = computed(() => {
    const viewers = this.accessLog()?.filter((al) => this.isClientViewer(al) && al.cases_accessed && al.cases_accessed.length > 0) ?? [];
    return orderBy(viewers, [(v) => this.getMaxLastAccessed(v)], ['desc']).map((viewer) => ({
      ...viewer,
      latestAccessDate: this.getLatestAccessDate(viewer),
    }));
  });

  isLoading = signal<boolean>(true);

  ngOnInit() {
    this.isLoading.set(true);
    this.locationService
      .getLocationAccessLog(this.branchIdNum(), this.locationIdNum())
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(
        (accessLog) => this.accessLog.set(accessLog),
        (err) => console.error('Error fetching data:', err),
      );
  }

  private getMaxLastAccessed(record: AccessLogRecord): number {
    if (!record.cases_accessed?.length) return 0;
    const timestamps = record.cases_accessed
      .map((ca) => {
        const ts = ca.last_accessed.match(/[+-]|Z/) ? ca.last_accessed : ca.last_accessed + 'Z';
        const dt = DateTime.fromISO(ts, { zone: 'utc' });
        return dt.isValid ? dt.toLocal().toMillis() : undefined;
      })
      .filter(Boolean);
    return Math.max(0, ...timestamps);
  }

  private getLatestAccessDate(record: AccessLogRecord): string {
    if (!record.cases_accessed?.length) return '';
    const latestCase = record.cases_accessed.reduce((latest, current) => {
      const currentTs = current.last_accessed.match(/[+-]|Z/) ? current.last_accessed : current.last_accessed + 'Z';
      const latestTs = latest.last_accessed.match(/[+-]|Z/) ? latest.last_accessed : latest.last_accessed + 'Z';
      const currentDt = DateTime.fromISO(currentTs, { zone: 'utc' });
      const latestDt = DateTime.fromISO(latestTs, { zone: 'utc' });
      return currentDt.toMillis() > latestDt.toMillis() ? current : latest;
    });
    return this.utcToLocal(latestCase.last_accessed);
  }

  private isBranchViewer(record: AccessLogRecord): boolean {
    return record.roles.some((role) => ['branch-owner', 'branch-manager', 'market-manager'].includes(role));
  }

  private isClientViewer(record: AccessLogRecord): boolean {
    return record.roles.includes('guest') && !this.isBranchViewer(record);
  }

  utcToLocal(utcDateString: string): string {
    const dt = DateTime.fromISO(utcDateString, { zone: 'utc' });
    return dt.isValid ? dt.toLocal().toISO() : utcDateString;
  }
}
